/**
 * BuyCreditsButton — Push 4-1ah (2026-07-10) thin wrapper.
 *
 * Sugar over the useBuyCredits() hook so surfaces that just want a "Top up"
 * button don't have to wire the modal + open handler themselves. Role-aware
 * label + reason-threaded copy in one line:
 *
 *   <BuyCreditsButton size="sm" reason="low_balance" />
 *
 * Under the hood: opens the appropriate modal (buy-flow for admin/owner,
 * request-topup for standard users) via the global provider.
 */

import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { useSession } from '../hooks/useSession.tsx';
import { useBuyCredits, type BuyCreditsReason } from './BuyCreditsProvider.tsx';

type ButtonSize = 'sm' | 'md';
type ButtonVariant = 'primary' | 'secondary' | 'link';

interface BuyCreditsButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'children'> {
  size?: ButtonSize;
  variant?: ButtonVariant;
  reason?: BuyCreditsReason;
  /** Optional custom label. Default label branches by role. */
  label?: ReactNode;
}

export function BuyCreditsButton({
  size = 'md',
  variant = 'primary',
  reason = 'proactive',
  label,
  className,
  ...rest
}: BuyCreditsButtonProps) {
  const { user } = useSession();
  const { open } = useBuyCredits();

  const isPrivileged = user?.role === 'owner' || user?.role === 'admin';
  const defaultLabel = isPrivileged ? 'Buy credits' : 'Request top-up';

  const classes = ['btn', `btn-${variant}`];
  if (size === 'sm') classes.push('btn-sm');
  if (className !== undefined && className !== '') classes.push(className);

  return (
    <button
      type="button"
      className={classes.join(' ')}
      onClick={() => open(reason)}
      {...rest}
    >
      {label ?? defaultLabel}
    </button>
  );
}
